import { DocumentTextIcon } from "@heroicons/react/24/outline";
import { ago, NoteEntityId, NoteId } from "@sp-crm/core";
import { NoteRowNote, useNoteRow } from "components/activity-feed/use-note-row";
import { EditNote } from "components/notes/edit";
import { NoteAuthor } from "components/notes/note-author";
import { DeleteButton } from "components/ui/action-button";
import { Badge } from "components/ui/badge";
import { Spinner } from "components/ui/spinner";
import { UpdateNoteMutation } from "generated/graphql";
import React from "react";
import {
    ActivityFeedRowBody,
    ActivityRowBody,
    ActivityRowEditButton,
    activityRowIconClasses,
} from "./activity-rows";

interface RecentActivityNoteRowProps {
    note: NoteRowNote;
    refetch: () => void;
    entityId: NoteEntityId;
    pinnedNoteId: NoteId | null;
    onDeleted: (noteId: NoteId) => void;
    onUpdated: (note: UpdateNoteMutation["updateNote"]) => void;
}

export const RecentActivityNoteRow: React.FC<RecentActivityNoteRowProps> = props => {
    const { note, entityId, pinnedNoteId, refetch, onDeleted, onUpdated } = props;
    const {
        isEditing,
        isDeleting,
        enableEdit,
        disableEdit,
        deleteCallback,
        updateCallback,
        editableNote,
    } = useNoteRow({ note, entityId, refetch, onDeleted, onUpdated });

    if (isEditing) {
        return (
            <ActivityRowBody
                leading={
                    <DocumentTextIcon
                        aria-hidden="true"
                        className={activityRowIconClasses}
                    />
                }
                actions={null}>
                <div className="space-y-2">
                    <EditNote
                        onEditNote={updateCallback}
                        note={editableNote}
                        pinnedNoteId={pinnedNoteId}
                        leaveEditMode={disableEdit}
                    />
                    <DeleteButton
                        backgroundColor="bg-white"
                        onClick={deleteCallback}
                        confirm={{
                            title: "Delete note?",
                            message: "Are you sure you want to delete this note?",
                        }}
                    />
                </div>
            </ActivityRowBody>
        );
    }

    return (
        <ActivityFeedRowBody
            display={{
                iconKind: "note",
                title: "Note",
                titleAccessory: note.isPinned ? (
                    <Badge type="highlight" value="Important" />
                ) : null,
                chip: null,
                body: note.text,
                expandableBody: null,
                note: null,
                byline: <NoteAuthor note={note} authorPrefix="" />,
                timestamp: `${ago(note.dateAdded, "")}${note.wasEdited ? " (edited)" : ""}`,
                link: isDeleting ? (
                    <div className="flex items-center space-x-2">
                        <span className="text-xs italic text-gray-500">Deleting...</span>
                        <Spinner />
                    </div>
                ) : null,
            }}
            actions={isDeleting ? null : <ActivityRowEditButton onClick={enableEdit} />}
        />
    );
};
